import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import { Teeth } from 'consts/autochartConsts'
import { useToothNumberingSystem } from 'hooks/useToothNumberingSystem'
import moment from 'moment'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatToothNumberLabel } from 'utils/autochartUtils'
import { exportToCsv } from 'utils/fileUtils'
import useAutochart from '../AutochartGrid/useAutochart'
import useConditionDescription from '../AutochartGrid/useConditionDescription'
import AutochartTableDescriptionCell from './AutochartTableDescriptionCell'
import AutochartTableNotesCell from './AutochartTableNotesCell'

export default ({ autochart }) => {
  const { t } = useTranslation()

  const { updateConditionNotes } = useAutochart()
  const { getPropertiesDescription } = useConditionDescription()

  const numberingSystem = useToothNumberingSystem()

  const columns = useMemo(() => {
    return [
      {
        name: 'date',
        label: t('dialogs.autochart.table.columns.date')
      },
      {
        name: 'toothLabel',
        label: t('dialogs.autochart.table.columns.toothNumber'),
        textAlign: 'center',
        render: row => <DazzedParagraph12 thickness="bold">{row.toothLabel}</DazzedParagraph12>
      },
      {
        name: 'description',
        label: t('dialogs.autochart.table.columns.description'),
        render: row => (
          <AutochartTableDescriptionCell
            label={row.description}
            createdByGrin={row.createdByGrin}
            editorId={row.editorId}
          />
        )
      },
      {
        name: 'surfaces',
        label: t('dialogs.autochart.table.columns.surfaces'),
        textAlign: 'center',
        render: row => (
          <DazzedParagraph12 thickness="bold" textAlign="center">
            {row.surfaces}
          </DazzedParagraph12>
        )
      },
      {
        name: 'notes',
        label: t('dialogs.autochart.table.columns.notes'),
        render: row => (
          <AutochartTableNotesCell
            notes={row.notes}
            onEdit={notes =>
              updateConditionNotes({ toothNumber: row.toothNumber, conditionType: row.conditionType, notes })
            }
          />
        )
      }
    ]
  }, [t, updateConditionNotes])

  const rows = useMemo(() => {
    if (!autochart.teeth) {
      return []
    }

    return Object.values(autochart.teeth)
      .filter(tooth => tooth.conditions?.length > 0)
      .map(tooth =>
        tooth.conditions.map(condition => {
          const properiesDescription = getPropertiesDescription({
            conditionType: condition.type,
            properties: condition.properties
          })
          const toothSurfacesOrder = Teeth[tooth.number]?.surfaces || []
          const orderedSurfaces = (condition.surfaces || [])
            .slice()
            .sort((a, b) => toothSurfacesOrder.indexOf(a) - toothSurfacesOrder.indexOf(b))

          return {
            conditionType: condition.type,
            toothNumber: tooth.number,
            toothLabel: formatToothNumberLabel({ universalNumber: tooth.number, system: numberingSystem }),
            date: moment(condition.updatedAt).format('MM/DD/YYYY'),
            description: `${t(`dialogs.autochart.conditions.${condition.type}`)} ${
              properiesDescription ? `(${properiesDescription})` : ''
            }${condition.indecisive ? `*` : ''}`,
            surfaces: !orderedSurfaces.length ? '-' : orderedSurfaces.join(''),
            notes: condition.note,
            createdByGrin: condition.editedBy === 'Grin',
            editorId: condition.editedBy !== 'Grin' ? condition.editedBy : null
          }
        })
      )
      .flat()
  }, [autochart, t, getPropertiesDescription, numberingSystem])

  const downloadTableCsv = useCallback(
    ({ fileName = 'autochart.csv' }) => {
      if (!rows.length) {
        return
      }

      const exportData = rows.map(row =>
        columns.reduce(
          (final, column) => ({
            ...final,
            [column.label]: row[column.name]
          }),
          {}
        )
      )

      exportToCsv({ data: exportData, fileName })
    },
    [rows, columns]
  )

  return {
    columns,
    rows,
    downloadTableCsv
  }
}
